export type HeldEvidenceReviewStatus =
  | 'awaiting_upload'
  | 'awaiting_release'
  | 'needs_assignment'
  | 'beyond_sla';

export type HeldEvidenceQueueSlice = 'review_ready' | 'upload_pending';

export const HELD_EVIDENCE_SERVER_SAFE_STATES = ['media_uploaded', 'acknowledged'] as const;

export function isHeldEvidenceReviewReadyState(state: string | null | undefined): boolean {
  return typeof state === 'string'
    && (HELD_EVIDENCE_SERVER_SAFE_STATES as readonly string[]).includes(state);
}

export type HeldEvidenceProposedTask = {
  assignmentId: number | null;
  campaignName: string | null;
  isAvailableForRelease: boolean;
};

export type HeldEvidenceSituationInput = {
  operatorName: string;
  storeName: string;
  heldStatus: HeldEvidenceReviewStatus;
  automaticBindingBlocker: string | null;
  proposedAssignmentId: number | null;
  assignmentId: number | null;
  proposedTask: HeldEvidenceProposedTask;
};

export const HELD_EVIDENCE_RELEASE_HELP =
  'If you release, the photo is attached to the task you select. Approve or reject that submission later in Campaign Tracker.';

export const HELD_EVIDENCE_PREVIEW_PENDING =
  'Photo not on server yet. The operator took this on their phone — it will appear here once upload finishes. There is nothing for you to review yet.';

export const HELD_EVIDENCE_WHY_HELD_INTRO =
  'The operator photographed work at a store, but the app could not attach that photo to their visit automatically — often while offline, or after a task or visit changed.';

export const HELD_EVIDENCE_AWAITING_UPLOAD_INTRO =
  'The operator has taken a photo on their phone, but it has not reached the server yet. This row is a heads-up only — you cannot review or release anything until the photo arrives.';

export const HELD_EVIDENCE_AWAITING_UPLOAD_NEXT =
  'Check back later. Once the photo appears below, you can decide whether to attach it to a task or discard it.';

export const HELD_EVIDENCE_DECISION_RELEASE =
  'Attach to task — you have seen the photo and it shows genuine posting work. Attach it to the correct task; it will then appear in Campaign Tracker for normal approve/reject.';

export const HELD_EVIDENCE_DECISION_ABANDON =
  'Discard — you have seen the photo and it should not be used (duplicate, mistake, test shot, or unusable). It leaves this queue and will not attach to any task.';

export const HELD_EVIDENCE_FALLBACK_CAMPAIGN_LABEL = 'Unknown task';

function formatTaskLabel(
  campaignName: string | null | undefined,
  assignmentId: number | null,
): string | null {
  const trimmed = campaignName?.trim();
  if (trimmed && trimmed !== HELD_EVIDENCE_FALLBACK_CAMPAIGN_LABEL && trimmed !== 'Held evidence') {
    return trimmed;
  }
  if (assignmentId != null) return `Task #${assignmentId}`;
  return null;
}

export function heldEvidenceDisplayCampaignName(input: {
  campaignName: string | null | undefined;
  proposedTask?: HeldEvidenceProposedTask;
  proposedAssignmentId?: number | null;
}): string {
  const fromProposed = input.proposedTask
    ? formatTaskLabel(input.proposedTask.campaignName, input.proposedTask.assignmentId)
    : null;
  if (fromProposed) return fromProposed;

  const fromRow = formatTaskLabel(input.campaignName, input.proposedAssignmentId ?? null);
  if (fromRow) return fromRow;

  return HELD_EVIDENCE_FALLBACK_CAMPAIGN_LABEL;
}

export function heldEvidenceModalTitle(bytesServerSafe: boolean): string {
  return bytesServerSafe ? 'Review held photo' : 'Waiting for photo';
}

export function heldEvidenceQueueTypeLabel(
  heldStatus: HeldEvidenceReviewStatus | undefined,
): string {
  if (heldStatus === 'awaiting_upload') return 'Upload pending';
  return 'Needs review';
}

export function describeHeldEvidenceOperatorFacts(input: Pick<
  HeldEvidenceSituationInput,
  'operatorName' | 'storeName' | 'proposedTask'
>): {
  operator: string;
  store: string;
  photographed: string;
} {
  const taskLabel = formatTaskLabel(
    input.proposedTask.campaignName,
    input.proposedTask.assignmentId,
  );

  return {
    operator: input.operatorName,
    store: input.storeName,
    photographed: taskLabel ?? 'Task not recorded on the phone',
  };
}

export function describeHeldEvidenceSituation(input: HeldEvidenceSituationInput): string {
  const taskLabel = formatTaskLabel(
    input.proposedTask.campaignName,
    input.proposedTask.assignmentId,
  );
  const taskForSentence = taskLabel ? `"${taskLabel}"` : 'a store task';
  const whoWhere = `${input.operatorName} at ${input.storeName}`;

  switch (input.heldStatus) {
    case 'awaiting_upload':
      return taskLabel
        ? `${whoWhere} photographed ${taskForSentence}, but the photo has not finished uploading. Wait for upload to complete before releasing.`
        : `${whoWhere} took a photo, but it has not finished uploading. Wait for upload to complete before releasing.`;
    case 'beyond_sla':
      return taskLabel
        ? `This photo of ${taskForSentence} from ${whoWhere} has been waiting too long to link automatically. Review the photo and release it to the correct task, or abandon it.`
        : `This photo from ${whoWhere} has been waiting too long to link to a store task. Review it and release or abandon it.`;
    case 'needs_assignment':
      if (input.automaticBindingBlocker === 'assignment_not_supplied') {
        return `${whoWhere} took a photo, but the phone did not record which task it was for. Look at the photo and choose the correct task below.`;
      }
      if (taskLabel && !input.proposedTask.isAvailableForRelease) {
        return `${whoWhere} photographed ${taskForSentence}, but that task is no longer active for this visit. Look at the photo and choose the correct active task below.`;
      }
      return `${whoWhere} took a photo, but the system could not confirm which task it belongs to. Choose the correct task below before releasing.`;
    case 'awaiting_release':
      if (taskLabel && input.assignmentId == null) {
        return `${whoWhere} photographed ${taskForSentence}, but the app could not attach it automatically — usually because the visit or task changed before sync finished. If the photo is genuine, release it to ${taskForSentence} (or pick another task if this one is wrong).`;
      }
      return `${whoWhere} has a photo ready to attach to a store task. Release it if genuine, or abandon if it should not count.`;
    default: {
      const exhaustive: never = input.heldStatus;
      throw new Error(`Unhandled held status: ${exhaustive}`);
    }
  }
}

export function deriveHeldEvidenceReviewStatus(input: {
  bytesServerSafe: boolean;
  automaticBindingBlocker: string | null;
  reconciliationStatus: string;
}): HeldEvidenceReviewStatus {
  if (!input.bytesServerSafe) return 'awaiting_upload';
  if (input.reconciliationStatus === 'beyond_reconciliation_sla') return 'beyond_sla';
  if (
    input.automaticBindingBlocker === 'assignment_not_supplied'
    || input.automaticBindingBlocker === 'proposed_assignment_not_yet_proven'
  ) {
    return 'needs_assignment';
  }
  return 'awaiting_release';
}

export function heldEvidenceReviewStatusLabel(status: HeldEvidenceReviewStatus): string {
  switch (status) {
    case 'awaiting_upload':
      return 'Photo not received yet';
    case 'needs_assignment':
      return 'Photo received — choose task';
    case 'beyond_sla':
      return 'Photo received — overdue';
    case 'awaiting_release':
      return 'Photo received — review needed';
    default: {
      const exhaustive: never = status;
      throw new Error(`Unhandled held status: ${exhaustive}`);
    }
  }
}

export function heldEvidenceUploadBadgeLabel(bytesServerSafe: boolean): string {
  return bytesServerSafe ? 'Photo received' : 'Upload in progress';
}

export function heldEvidenceBlockerMessage(
  blocker: string | null,
  proposedTask?: HeldEvidenceProposedTask,
): string | null {
  const taskLabel = proposedTask
    ? formatTaskLabel(proposedTask.campaignName, proposedTask.assignmentId)
    : null;

  switch (blocker) {
    case 'photo_bytes_not_yet_verified':
      return taskLabel
        ? `The photo for "${taskLabel}" is still uploading. Wait for the upload to finish before releasing.`
        : 'The photo is still uploading. Wait for the upload to finish before releasing.';
    case 'assignment_not_supplied':
      return 'No task was recorded when this photo was taken. Look at the photo and choose which task it belongs to below.';
    case 'proposed_assignment_not_yet_proven':
      // A missing binding is not proof that the task is inactive. The review
      // detail's current release-target lookup is authoritative for availability.
      if (proposedTask?.isAvailableForRelease) {
        return taskLabel
          ? `"${taskLabel}" is available for this visit, but this photo has not been attached automatically. Review the photo and confirm the correct task below.`
          : 'The task recorded on the phone is available for this visit, but this photo has not been attached automatically. Review the photo and confirm the correct task below.';
      }
      if (!proposedTask) {
        return 'The task recorded on the phone could not be confirmed for this visit. Review the photo and choose the correct task below.';
      }
      return taskLabel
        ? `"${taskLabel}" is what the operator was working on, but it is not currently available for release in this visit. Choose the correct active task below if the photo is still valid.`
        : 'The task recorded on the phone is not currently available for release in this visit. Choose the correct active task below.';
    default:
      return blocker;
  }
}
